import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { classifyWineSuiteDeviceType } from '@util/dws-device-classification';
import { WsTooltipService } from './ws-tooltip.service';
import { normalizeWsSuperadminCopyValue } from './ws-superadmin-copy/ws-superadmin-copy.util';

@Directive({
  selector: '[wsAdminTooltip]',
  host: { class: 'ws-admin-tooltip-host' },
})
export class WsAdminTooltipDirective implements OnInit, OnDestroy {
  @Input('wsAdminTooltip') value: string | number | null = null;
  @Input() wsAdminTooltipDisabled = false;
  @Input() wsAdminTooltipShowDelay = 200;
  @Input() wsAdminTooltipClass = '';

  private hoverTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly isTouchMode = classifyWineSuiteDeviceType() === 'tablet';

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private tooltipService: WsTooltipService
  ) {}

  ngOnInit(): void {
    if (this.isTouchMode) {
      this.elementRef.nativeElement.style.cursor = 'pointer';
    }
  }

  ngOnDestroy(): void {
    this.clearHoverTimer();
    if (this.tooltipService.isOpenFor(this.elementRef.nativeElement)) {
      this.tooltipService.closeAll();
    }
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    if (this.isTouchMode || this.isDisabled) {
      return;
    }
    this.clearHoverTimer();
    this.hoverTimer = setTimeout(() => {
      this.openTooltip();
    }, this.wsAdminTooltipShowDelay);
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    if (this.isTouchMode) {
      return;
    }
    this.clearHoverTimer();
    this.tooltipService.scheduleClose();
  }

  @HostListener('touchstart', ['$event'])
  onTouchStart(event: TouchEvent): void {
    if (!this.isTouchMode || this.isDisabled) {
      return;
    }
    event.stopPropagation();
    if (this.tooltipService.isOpenFor(this.elementRef.nativeElement)) {
      this.tooltipService.closeAll();
    } else {
      this.openTooltip();
    }
  }

  private get isDisabled(): boolean {
    return this.wsAdminTooltipDisabled || !normalizeWsSuperadminCopyValue(this.value);
  }

  private openTooltip(): void {
    const text = normalizeWsSuperadminCopyValue(this.value);
    if (!text) {
      return;
    }
    this.tooltipService.show(
      this.elementRef.nativeElement,
      text,
      this.isDisabled,
      this.wsAdminTooltipClass,
      null,
      true
    );
  }

  private clearHoverTimer(): void {
    if (this.hoverTimer) {
      clearTimeout(this.hoverTimer);
      this.hoverTimer = null;
    }
  }
}
